import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Add, Edit, Visibility, Print, Refresh } from "@mui/icons-material";
import { useSelector } from "react-redux";
import api from "../../../api/axiosInterceptor";
import { SnackBarComponent } from "../../../Components/snackBar/snackBar";
import { LoadingSpinner } from "../../../Components/spinner/spinner";
import TableComponent from "../../../Components/TableComponent/TableComponent";
import {
  TODAY,
  StatusChip,
  FG,
  TF,
  SecHead,
  FYSelect,
  RowTable,
  ApprovalTimeline,
} from "./component/sharedForm";
import ApprovalPanel from "./approvalPanel";

// ── Row blanks ────────────────────────────────────────────────────────────────
const RB = {
  2: {
    file_collection_number: "",
    date_of_recording: "",
    name: "",
    designation_employee: "",
    remarks: "",
  },
  5: {
    file_no: "",
    classification_class: "",
    weeded_out: "",
    downgraded: "",
    upgraded: "",
    date_of_review: "",
  },
  6: {
    file_number: "",
    year: "",
    subject: "",
    department_section: "",
    destroyed_or_kept: "",
    signature_section_officer: "",
  },
  7: {
    number_files_recorded: "",
    number_files_indexed: "",
    number_files_weeded_out: "",
    number_files_reviewed: "",
    remarks: "",
  },
  8: {
    file_collection_number: "",
    file_number: "",
    year: "",
    date_of_initiation: "",
    date_of_closure: "",
    subject_matter_brief: "",
    subject_description_brief: "",
    classification: "",
    approval_of_classification_by: "",
    date_sending_to_record: "",
    date_of_destruction: "",
    section: "",
    signature_date_os: "",
    record_room_serial_no: "",
    record_room_bag: "",
    rack_no: "",
    name_record_room_officer: "",
    signature_record_room_officer: "",
    date_record_room_officer: "",
    issued_to: "",
    designation: "",
    date_issued: "",
    issued_for_reference: "",
    date_returned: "",
    signature: "",
    date_of_record_room: "",
  },
};

// ── Form 1 ────────────────────────────────────────────────────────────────────
const BLANK1 = {
  year: "",
  total_files_a_category: "",
  files_transferred_in_year: "",
  files_referred_columns_1_2: "",
  files_due_for_appraisal: "",
  remarks: "",
  date: TODAY,
  record_officer: "",
};

const Form1 = ({ d, ro, set }) => (
  <Box>
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" fontWeight="bold" mb={0.5}>
        Particulars of Records of Permanent Nature
      </Typography>
      <Box sx={{ width: 300 }}>
        <FYSelect
          value={d.year}
          onChange={ro ? null : (v) => set("year", v)}
          disabled={ro}
          label="Year"
        />
      </Box>
    </Box>
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: 1,
        overflow: "hidden",
        mb: 2,
      }}
    >
      <Box
        sx={{
          background: "#1976d2",
          color: "#fff",
          px: 2,
          py: 1,
          display: "grid",
          gridTemplateColumns: "70px 1fr 240px",
          gap: 1,
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          Sl.No
        </Typography>
        <Typography variant="body2" fontWeight="bold"></Typography>
        <Typography variant="body2" fontWeight="bold"></Typography>
      </Box>
      {[
        {
          sl: 1,
          label: "Total Files of A Category in Record Room",
          field: "total_files_a_category",
        },
        {
          sl: 2,
          label:
            "Total Files of A Category transferred to Record Room by Sections in the Year",
          field: "files_transferred_in_year",
        },
        {
          sl: 3,
          label: "Total Files referred to in Columns 1 & 2",
          field: "files_referred_columns_1_2",
        },
        {
          sl: 4,
          label: "Total Files due for Appraisal",
          field: "files_due_for_appraisal",
        },
      ].map((r, i) => (
        <Box
          key={r.field}
          sx={{
            display: "grid",
            gridTemplateColumns: "70px 1fr 240px",
            gap: 1,
            borderTop: "1px solid #eee",
            px: 2,
            py: 1,
            alignItems: "center",
            background: i % 2 === 0 ? "#fafafa" : "#fff",
          }}
        >
          <Typography variant="body2">{r.sl}</Typography>
          <Typography variant="body2">{r.label}</Typography>
          <TF
            value={d[r.field]}
            disabled={ro}
            onChange={ro ? null : (v) => set(r.field, v)}
          />
        </Box>
      ))}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "70px 1fr 240px",
          gap: 1,
          borderTop: "1px solid #eee",
          px: 2,
          py: 1,
          background: "#fff",
        }}
      >
        <Typography variant="body2">5</Typography>
        <Typography variant="body2">Remarks</Typography>
        <TF
          value={d.remarks}
          disabled={ro}
          multiline
          rows={3}
          onChange={ro ? null : (v) => set("remarks", v)}
        />
      </Box>
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Typography variant="body2">Date:</Typography>
        <TF
          type="date"
          value={d.date}
          disabled={ro}
          onChange={ro ? null : (v) => set("date", v)}
          sx={{ width: 180 }}
        />
      </Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Typography variant="body2">Record Officer:</Typography>
        <TF
          value={d.record_officer}
          disabled={ro}
          onChange={ro ? null : (v) => set("record_officer", v)}
          sx={{ width: 240 }}
        />
      </Box>
    </Box>
  </Box>
);

// ── Form 2 ────────────────────────────────────────────────────────────────────
const BLANK2 = {
  department_unit: "",
  section: "",
  date_sent_to_records: TODAY,
  classification: "",
  disposed_by: "",
};
const COLS2 = [
  {
    field: "file_collection_number",
    label: "File (Collection) Number",
    width: 180,
  },
  {
    field: "date_of_recording",
    label: "Date of Recording",
    type: "date",
    width: 160,
  },
  { field: "name", label: "Name", width: 160 },
  {
    field: "designation_employee",
    label: "Designation of employee restoring the record to bundle shelf",
    width: 280,
  },
  { field: "remarks", label: "Remarks", multiline: true, width: 180 },
];

const Form2 = ({ d, ro, set, rows, setRows }) => (
  <Box>
    <FG cols={3}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Department / Unit
        </Typography>
        <TF
          value={d.department_unit}
          disabled={ro}
          onChange={ro ? null : (v) => set("department_unit", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Section
        </Typography>
        <TF
          value={d.section}
          disabled={ro}
          onChange={ro ? null : (v) => set("section", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Date on which sent to Records Section
        </Typography>
        <TF
          type="date"
          value={d.date_sent_to_records}
          disabled={ro}
          onChange={ro ? null : (v) => set("date_sent_to_records", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Classification
        </Typography>
        <TF
          value={d.classification}
          disabled={ro}
          onChange={ro ? null : (v) => set("classification", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Disposed by
        </Typography>
        <TF
          value={d.disposed_by}
          disabled={ro}
          onChange={ro ? null : (v) => set("disposed_by", v)}
        />
      </Box>
    </FG>
    <SecHead label="Sl.No" />
    <RowTable
      columns={COLS2}
      rows={rows}
      readOnly={ro}
      onAdd={() => setRows((r) => [...r, { ...RB[2] }])}
      onUpdate={(i, f, v) =>
        setRows((r) => r.map((rw, ri) => (ri === i ? { ...rw, [f]: v } : rw)))
      }
      onDel={(i) => setRows((r) => r.filter((_, ri) => ri !== i))}
    />
  </Box>
);

// ── Form 3 ────────────────────────────────────────────────────────────────────
const BLANK3 = {
  file_order_no: "",
  serial_no: "",
  to_be_put_for_diary_file_no: "",
  name_of_requisitioning: "",
  signature_requisitioning: "",
  department_unit: "",
  section: "",
  counter_signature: "",
  designation: "",
};

const Form3 = ({ d, ro, set }) => (
  <Box>
    <FG cols={3}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          File / Order No
        </Typography>
        <TF
          value={d.file_order_no}
          disabled={ro}
          onChange={ro ? null : (v) => set("file_order_no", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Serial No
        </Typography>
        <TF
          value={d.serial_no}
          disabled={ro}
          onChange={ro ? null : (v) => set("serial_no", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          To be put up for Diary / File No
        </Typography>
        <TF
          value={d.to_be_put_for_diary_file_no}
          disabled={ro}
          onChange={ro ? null : (v) => set("to_be_put_for_diary_file_no", v)}
        />
      </Box>
    </FG>
    <FG cols={2}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Name of Requisitioning Person
        </Typography>
        <TF
          value={d.name_of_requisitioning}
          disabled={ro}
          onChange={ro ? null : (v) => set("name_of_requisitioning", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Signature of Requisitioning Person
        </Typography>
        <TF
          value={d.signature_requisitioning}
          disabled={ro}
          onChange={ro ? null : (v) => set("signature_requisitioning", v)}
        />
      </Box>
    </FG>
    <FG cols={3}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Department / Unit
        </Typography>
        <TF
          value={d.department_unit}
          disabled={ro}
          onChange={ro ? null : (v) => set("department_unit", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Section
        </Typography>
        <TF
          value={d.section}
          disabled={ro}
          onChange={ro ? null : (v) => set("section", v)}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Counter Signature*
        </Typography>
        <TF
          value={d.counter_signature}
          disabled={ro}
          onChange={ro ? null : (v) => set("counter_signature", v)}
        />
      </Box>
    </FG>
    <Box sx={{ mb: 2, width: "34%" }}>
      <Typography variant="caption" color="text.secondary">
        Designation
      </Typography>
      <TF
        value={d.designation}
        disabled={ro}
        onChange={ro ? null : (v) => set("designation", v)}
      />
    </Box>
    <Box
      sx={{
        p: 1.5,
        background: "#fff8e1",
        borderRadius: 1,
        border: "1px solid #ffe082",
      }}
    >
      <Typography variant="caption" color="warning.dark">
        *Note: In case of Secret / Confidential / Personal Files, Admin Officer
        must counter sign
      </Typography>
    </Box>
  </Box>
);

// ── Form 4 ────────────────────────────────────────────────────────────────────
const BLANK4 = {
  total_classified_records: "",
  total_classified_to_be_reviewed: "",
  total_classified_reviewed_downgraded: "",
  balance_to_be_reviewed_downgraded: "",
  remarks: "",
};

const Form4 = ({ d, ro, set }) => (
  <Box>
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: 1,
        overflow: "hidden",
        mb: 2,
      }}
    >
      <Box
        sx={{
          background: "#1976d2",
          color: "#fff",
          px: 2,
          py: 1,
          display: "grid",
          gridTemplateColumns: "70px 1fr 240px",
          gap: 1,
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          Sl.No
        </Typography>
        <Typography variant="body2" fontWeight="bold"></Typography>
        <Typography variant="body2" fontWeight="bold"></Typography>
      </Box>
      {[
        {
          sl: 1,
          label: "Total Classified Records",
          field: "total_classified_records",
        },
        {
          sl: 2,
          label: "Total Classified Records to be reviewed",
          field: "total_classified_to_be_reviewed",
        },
        {
          sl: 3,
          label: "Total Classified Records reviewed & downgraded",
          field: "total_classified_reviewed_downgraded",
        },
        {
          sl: 4,
          label: "Balance to be reviewed & downgraded",
          field: "balance_to_be_reviewed_downgraded",
        },
      ].map((r, i) => (
        <Box
          key={r.field}
          sx={{
            display: "grid",
            gridTemplateColumns: "70px 1fr 240px",
            gap: 1,
            borderTop: "1px solid #eee",
            px: 2,
            py: 1,
            alignItems: "center",
            background: i % 2 === 0 ? "#fafafa" : "#fff",
          }}
        >
          <Typography variant="body2">{r.sl}</Typography>
          <Typography variant="body2" fontWeight="bold">
            {r.label}
          </Typography>
          <TF
            value={d[r.field]}
            disabled={ro}
            onChange={ro ? null : (v) => set(r.field, v)}
          />
        </Box>
      ))}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "70px 1fr 240px",
          gap: 1,
          borderTop: "1px solid #eee",
          px: 2,
          py: 1,
          background: "#fff",
        }}
      >
        <Typography variant="body2">5</Typography>
        <Typography variant="body2" fontWeight="bold">
          Remarks
        </Typography>
        <TF
          value={d.remarks}
          disabled={ro}
          multiline
          rows={3}
          onChange={ro ? null : (v) => set("remarks", v)}
        />
      </Box>
    </Box>
  </Box>
);

// ── Form 5 ────────────────────────────────────────────────────────────────────
const BLANK5 = { number: "" };
const COLS5 = [
  { field: "file_no", label: "File No.", width: 140 },
  { field: "classification_class", label: "Class", width: 100 },
  { field: "weeded_out", label: "Weeded Out", width: 120 },
  { field: "downgraded", label: "Downgraded", width: 120 },
  { field: "upgraded", label: "Upgraded", width: 120 },
  {
    field: "date_of_review",
    label: "Date of Review",
    type: "date",
    width: 160,
  },
];

const Form5 = ({ d, ro, set, rows, setRows }) => (
  <Box>
    <Box sx={{ mb: 2, width: 220 }}>
      <Typography variant="caption" color="text.secondary">
        No.
      </Typography>
      <TF
        value={d.number}
        disabled={ro}
        onChange={ro ? null : (v) => set("number", v)}
      />
    </Box>
    <SecHead label="Sl.No" />
    <Box
      sx={{
        p: 1.5,
        background: "#e3f2fd",
        borderRadius: 1,
        border: "1px solid #90caf9",
        mb: 1,
      }}
    >
      <Typography variant="caption" fontWeight="bold" color="primary">
        Instructions of Reviewing Authority
      </Typography>
    </Box>
    <RowTable
      columns={COLS5}
      rows={rows}
      readOnly={ro}
      onAdd={() => setRows((r) => [...r, { ...RB[5] }])}
      onUpdate={(i, f, v) =>
        setRows((r) => r.map((rw, ri) => (ri === i ? { ...rw, [f]: v } : rw)))
      }
      onDel={(i) => setRows((r) => r.filter((_, ri) => ri !== i))}
    />
  </Box>
);

// ── Form 6 ────────────────────────────────────────────────────────────────────
const COLS6 = [
  { field: "file_number", label: "File Number", width: 140 },
  { field: "year", label: "Year", width: 100 },
  { field: "subject", label: "Subject", width: 200, multiline: true },
  { field: "department_section", label: "Department / Section", width: 180 },
  {
    field: "destroyed_or_kept",
    label: "Destroyed or kept for Destruction",
    width: 220,
  },
  {
    field: "signature_section_officer",
    label: "Signature of Section Officer / OS",
    width: 220,
  },
];
const Form6 = ({ rows, setRows, ro }) => (
  <Box>
    <SecHead label="Sl.No" />
    <RowTable
      columns={COLS6}
      rows={rows}
      readOnly={ro}
      onAdd={() => setRows((r) => [...r, { ...RB[6] }])}
      onUpdate={(i, f, v) =>
        setRows((r) => r.map((rw, ri) => (ri === i ? { ...rw, [f]: v } : rw)))
      }
      onDel={(i) => setRows((r) => r.filter((_, ri) => ri !== i))}
    />
  </Box>
);

// ── Form 7 ────────────────────────────────────────────────────────────────────
const COLS7 = [
  {
    field: "number_files_recorded",
    label: "Number of Files Recorded",
    width: 200,
  },
  {
    field: "number_files_indexed",
    label: "Number of Files Indexed",
    width: 200,
  },
  {
    field: "number_files_weeded_out",
    label: "Number of Files Weeded Out",
    width: 200,
  },
  {
    field: "number_files_reviewed",
    label: "Number of Files Reviewed",
    width: 200,
  },
  { field: "remarks", label: "Remarks", multiline: true, width: 200 },
];
const Form7 = ({ rows, setRows, ro }) => (
  <Box>
    <SecHead label="Sl.No" />
    <RowTable
      columns={COLS7}
      rows={rows}
      readOnly={ro}
      onAdd={() => setRows((r) => [...r, { ...RB[7] }])}
      onUpdate={(i, f, v) =>
        setRows((r) => r.map((rw, ri) => (ri === i ? { ...rw, [f]: v } : rw)))
      }
      onDel={(i) => setRows((r) => r.filter((_, ri) => ri !== i))}
    />
  </Box>
);

// ── Form 8 ────────────────────────────────────────────────────────────────────
const Form8Entry = ({ row, idx, ro, onChange }) => {
  const s = (f, v) => onChange(idx, f, v);
  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 2,
        mb: 2,
        background: idx % 2 === 0 ? "#fafafa" : "#fff",
      }}
    >
      <Box
        sx={{
          background: "#1976d2",
          color: "#fff",
          px: 2,
          py: 0.5,
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          {idx + 1}
        </Typography>
      </Box>
      <FG cols={3}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            File (Collection) Number
          </Typography>
          <TF
            value={row.file_collection_number}
            disabled={ro}
            onChange={ro ? null : (v) => s("file_collection_number", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            File Number
          </Typography>
          <TF
            value={row.file_number}
            disabled={ro}
            onChange={ro ? null : (v) => s("file_number", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Year
          </Typography>
          <FYSelect
            value={row.year}
            onChange={ro ? null : (v) => s("year", v)}
            disabled={ro}
            label="Year"
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Date of initiation
          </Typography>
          <TF
            type="date"
            value={row.date_of_initiation}
            disabled={ro}
            onChange={ro ? null : (v) => s("date_of_initiation", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Date of Closure
          </Typography>
          <TF
            type="date"
            value={row.date_of_closure}
            disabled={ro}
            onChange={ro ? null : (v) => s("date_of_closure", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Classification (A/B/C/D)
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={row.classification || ""}
              disabled={ro}
              displayEmpty
              onChange={(e) => !ro && s("classification", e.target.value)}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {["A", "B", "C", "D"].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Approval of Classification By
          </Typography>
          <TF
            value={row.approval_of_classification_by}
            disabled={ro}
            onChange={ro ? null : (v) => s("approval_of_classification_by", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Date of sending to Record
          </Typography>
          <TF
            type="date"
            value={row.date_sending_to_record}
            disabled={ro}
            onChange={ro ? null : (v) => s("date_sending_to_record", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Date of destruction
          </Typography>
          <TF
            type="date"
            value={row.date_of_destruction}
            disabled={ro}
            onChange={ro ? null : (v) => s("date_of_destruction", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Section
          </Typography>
          <TF
            value={row.section}
            disabled={ro}
            onChange={ro ? null : (v) => s("section", v)}
          />
        </Box>
        <Box sx={{ gridColumn: "span 2" }}>
          <Typography variant="caption" color="text.secondary">
            Signature & date of OS of Section sending the file
          </Typography>
          <TF
            value={row.signature_date_os}
            disabled={ro}
            onChange={ro ? null : (v) => s("signature_date_os", v)}
          />
        </Box>
      </FG>
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Subject matter in brief (15 words maximum)
        </Typography>
        <TF
          value={row.subject_matter_brief}
          disabled={ro}
          onChange={ro ? null : (v) => s("subject_matter_brief", v)}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Subject description in brief (50 to 100 words)
        </Typography>
        <TF
          value={row.subject_description_brief}
          disabled={ro}
          multiline
          rows={3}
          onChange={ro ? null : (v) => s("subject_description_brief", v)}
        />
      </Box>
      <Box
        sx={{ background: "#e3f2fd", px: 2, py: 0.5, borderRadius: 1, mb: 1 }}
      >
        <Typography variant="body2" fontWeight="bold" color="primary">
          Receipt & Recording
        </Typography>
      </Box>
      <FG cols={3}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Record Room Serial No
          </Typography>
          <TF
            value={row.record_room_serial_no}
            disabled={ro}
            onChange={ro ? null : (v) => s("record_room_serial_no", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Record Room Bag
          </Typography>
          <TF
            value={row.record_room_bag}
            disabled={ro}
            onChange={ro ? null : (v) => s("record_room_bag", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Rack No
          </Typography>
          <TF
            value={row.rack_no}
            disabled={ro}
            onChange={ro ? null : (v) => s("rack_no", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Name of Record Room Officer
          </Typography>
          <TF
            value={row.name_record_room_officer}
            disabled={ro}
            onChange={ro ? null : (v) => s("name_record_room_officer", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Signature of Record Room Officer
          </Typography>
          <TF
            value={row.signature_record_room_officer}
            disabled={ro}
            onChange={ro ? null : (v) => s("signature_record_room_officer", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Date of Record Room Officer
          </Typography>
          <TF
            type="date"
            value={row.date_record_room_officer}
            disabled={ro}
            onChange={ro ? null : (v) => s("date_record_room_officer", v)}
          />
        </Box>
      </FG>
      <Box
        sx={{
          background: "#e3f2fd",
          px: 2,
          py: 0.5,
          borderRadius: 1,
          mb: 1,
          mt: 1,
        }}
      >
        <Typography variant="body2" fontWeight="bold" color="primary">
          Issue Note
        </Typography>
      </Box>
      <FG cols={3}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Issued To
          </Typography>
          <TF
            value={row.issued_to || ""}
            disabled={ro}
            onChange={ro ? null : (v) => s("issued_to", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Designation
          </Typography>
          <TF
            value={row.designation || ""}
            disabled={ro}
            onChange={ro ? null : (v) => s("designation", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Date Issued
          </Typography>
          <TF
            type="date"
            value={row.date_issued || ""}
            disabled={ro}
            onChange={ro ? null : (v) => s("date_issued", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Issued for Reference / Copying
          </Typography>
          <TF
            value={row.issued_for_reference || ""}
            disabled={ro}
            onChange={ro ? null : (v) => s("issued_for_reference", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Date Returned
          </Typography>
          <TF
            type="date"
            value={row.date_returned || ""}
            disabled={ro}
            onChange={ro ? null : (v) => s("date_returned", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Signature
          </Typography>
          <TF
            value={row.signature || ""}
            disabled={ro}
            onChange={ro ? null : (v) => s("signature", v)}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Date of Record Room
          </Typography>
          <TF
            type="date"
            value={row.date_of_record_room || ""}
            disabled={ro}
            onChange={ro ? null : (v) => s("date_of_record_room", v)}
          />
        </Box>
      </FG>
    </Box>
  );
};

const Form8 = ({ rows, setRows, ro }) => (
  <Box>
    <SecHead label="Sl.No" />
    {rows.map((row, idx) => (
      <Form8Entry
        key={idx}
        row={row}
        idx={idx}
        ro={ro}
        onChange={(i, f, v) =>
          setRows((r) => r.map((rw, ri) => (ri === i ? { ...rw, [f]: v } : rw)))
        }
      />
    ))}
    {!ro && (
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={() => setRows((r) => [...r, { ...RB[8] }])}
        >
          +
        </Button>
      </Box>
    )}
  </Box>
);

// ── Config & table columns ─────────────────────────────────────────────────────
const CONFIGS = [
  {
    num: 1,
    tabLabel: "Form 1",
    fullLabel: "Form 1 (Sub Rule 2 of Rule 5)",
    sub: "Particulars of Records of Permanent Nature",
    ep: "/legal/public-records/form1/",
    hasRows: false,
    blank: BLANK1,
  },
  {
    num: 2,
    tabLabel: "Form 2",
    fullLabel: "Form 2 (Sub Rule 3 of Rule 5)",
    sub: "List - Register of Record Files (Prepare in duplicate)",
    ep: "/legal/public-records/form2/",
    hasRows: true,
    blank: BLANK2,
  },
  {
    num: 3,
    tabLabel: "Form 3",
    fullLabel: "Form 3 (Sub Rule 1 of Rule 6)",
    sub: "Requisition Slip",
    ep: "/legal/public-records/form3/",
    hasRows: false,
    blank: BLANK3,
  },
  {
    num: 4,
    tabLabel: "Form 4",
    fullLabel: "Form 4 (Sub Rule 5 of Rule 8)",
    sub: "Half yearly Statement on periodical review of records",
    ep: "/legal/public-records/form4/",
    hasRows: false,
    blank: BLANK4,
  },
  {
    num: 5,
    tabLabel: "Form 5",
    fullLabel: "Form 5",
    sub: "Review and Weeding out of Records",
    ep: "/legal/public-records/form5/",
    hasRows: true,
    blank: BLANK5,
  },
  {
    num: 6,
    tabLabel: "Form 6",
    fullLabel: "Form 6 (Sub Rule 1 & 3 of Rule 9)",
    sub: "Register of Destruction / Preservation of Time Barred records",
    ep: "/legal/public-records/form6/",
    hasRows: true,
    blank: {},
  },
  {
    num: 7,
    tabLabel: "Form 7",
    fullLabel: "Form 7 (Sub Rule 4 of Rule 9)",
    sub: "Half Yearly Report on Recording, Indexing, Weeding",
    ep: "/legal/public-records/form7/",
    hasRows: true,
    blank: {},
  },
  {
    num: 8,
    tabLabel: "Form 8",
    fullLabel: "Form 8",
    sub: "File Record Note",
    ep: "/legal/public-records/form8/",
    hasRows: true,
    blank: {},
  },
];

const STATUS_LABEL = {
  draft: "Draft",
  submitted: "Submitted",
  approved_l1: "Approved (Shirastedar)",
  rejected_l1: "Rejected (Shirastedar)",
  approved_l2: "Approved (Tahsildar)",
  rejected_l2: "Rejected (Tahsildar)",
  approved_l3: "Approved (CA)",
  rejected_l3: "Rejected (CA)",
  final: "Finalised",
};
const EDITABLE = ["draft", "rejected_l1", "rejected_l2", "rejected_l3"];
const CAN_ADD = [ "super_staff"];

const TC = {
  1: [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    { id: "year", field: "year", headerName: "Financial Year", width: 140 },
    { id: "date", field: "date", headerName: "Date", width: 120 },
    {
      id: "record_officer",
      field: "record_officer",
      headerName: "Record Officer",
      width: 200,
    },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
  ],
  2: [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    {
      id: "department_unit",
      field: "department_unit",
      headerName: "Department / Unit",
      width: 200,
    },
    { id: "section", field: "section", headerName: "Section", width: 140 },
    {
      id: "date_sent_to_records",
      field: "date_sent_to_records",
      headerName: "Date Sent",
      width: 130,
    },
    {
      id: "classification",
      field: "classification",
      headerName: "Classification",
      width: 140,
    },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
  ],
  3: [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    {
      id: "file_order_no",
      field: "file_order_no",
      headerName: "File/Order No",
      width: 150,
    },
    {
      id: "name_of_requisitioning",
      field: "name_of_requisitioning",
      headerName: "Requisitioned By",
      width: 200,
    },
    {
      id: "designation",
      field: "designation",
      headerName: "Designation",
      width: 160,
    },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
  ],
  4: [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    {
      id: "total_classified_records",
      field: "total_classified_records",
      headerName: "Total Classified",
      width: 160,
    },
    {
      id: "balance_to_be_reviewed_downgraded",
      field: "balance_to_be_reviewed_downgraded",
      headerName: "Balance to Review",
      width: 180,
    },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
  ],
  5: [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    { id: "number", field: "number", headerName: "Number", width: 120 },
    { id: "row_count", field: "row_count", headerName: "Rows", width: 90 },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
  ],
  6: [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    { id: "row_count", field: "row_count", headerName: "Rows", width: 90 },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
  ],
  7: [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    { id: "row_count", field: "row_count", headerName: "Rows", width: 90 },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
  ],
  8: [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    { id: "row_count", field: "row_count", headerName: "Entries", width: 90 },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
  ],
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export const PublicRecordFormsComponent = () => {
  const role = useSelector((s) => s.auth.role);
  const [tab, setTab] = useState(0);
  const [view, setView] = useState("list");
  const [list, setList] = useState([]);
  const [listLoading, setLL] = useState(false);
  const [formData, setFormData] = useState({});
  const [formRows, setFormRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const cfg = CONFIGS[tab];

  const fetchList = useCallback(async () => {
    setLL(true);
    try {
      const res = await api.get(cfg.ep);
      setList(res.data);
    } catch {
      setSnack({ open: true, message: "Failed to load", severity: "error" });
    } finally {
      setLL(false);
    }
  }, [cfg.ep]);

  useEffect(() => {
    fetchList();
    setView("list");
  }, [tab]);

  const setField = (f, v) => setFormData((d) => ({ ...d, [f]: v }));

  const openNew = () => {
    setFormData({ ...cfg.blank });
    setFormRows(cfg.hasRows ? [{ ...RB[cfg.num] }] : []);
    setSelected(null);
    setView("form");
  };

  const openEdit = (item) => {
    setFormData({ ...item });
    setFormRows(item.rows || []);
    setSelected(item);
    setView("form");
  };

  const openView = async (item) => {
    try {
      const res = await api.get(`${cfg.ep}${item.id}/`);
      setFormData(res.data);
      setFormRows(res.data.rows || []);
      setSelected(res.data);
    } catch {
      setFormData(item);
      setFormRows(item.rows || []);
      setSelected(item);
    }
    setView("detail");
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...formData };
    if (cfg.hasRows) payload.rows = formRows;
    try {
      if (selected) {
        await api.patch(`${cfg.ep}${selected.id}/`, payload);
        setSnack({
          open: true,
          message: "Updated successfully",
          severity: "success",
        });
      } else {
        await api.post(cfg.ep, payload);
        setSnack({
          open: true,
          message: "Saved successfully",
          severity: "success",
        });
      }
      fetchList();
      setView("list");
    } catch (e) {
      setSnack({
        open: true,
        message: JSON.stringify(e.response?.data) || "Save failed",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderFields = (ro) => {
    const p = {
      d: formData,
      ro,
      set: setField,
      rows: formRows,
      setRows: setFormRows,
    };
    switch (cfg.num) {
      case 1:
        return <Form1 {...p} />;
      case 2:
        return <Form2 {...p} />;
      case 3:
        return <Form3 {...p} />;
      case 4:
        return <Form4 {...p} />;
      case 5:
        return <Form5 {...p} />;
      case 6:
        return <Form6 rows={formRows} setRows={setFormRows} ro={ro} />;
      case 7:
        return <Form7 rows={formRows} setRows={setFormRows} ro={ro} />;
      case 8:
        return <Form8 rows={formRows} setRows={setFormRows} ro={ro} />;
      default:
        return null;
    }
  };

  const tableRows = list.map((item, i) => ({
    ...item,
    slNo: i + 1,
    statusLabel: STATUS_LABEL[item.status] || item.status,
    row_count: item.rows?.length || 0,
  }));
  const actionCol = {
    id: "actions",
    field: "actions",
    headerName: "Actions",
    width: 120,
    renderCell: (p) => (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="View">
          <IconButton size="small" onClick={() => openView(p.row)}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* {EDITABLE.includes(p.row.status) && ( */}
        {EDITABLE.includes(p.row.status) && CAN_ADD.includes(role) && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => openEdit(p.row)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        borderRadius: 2,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          background: "rgba(25,118,210,0.04)",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Government of Karnataka – SPLOCA Public Record Forms
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Flow: Case Worker → Shirastedar → Tahsildar → CA / Admin
        </Typography>
      </Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: "1px solid #e0e0e0",
          "& .MuiTab-root": { fontSize: "0.75rem", minHeight: 44 },
        }}
      >
        {CONFIGS.map((c, i) => (
          <Tab key={i} label={c.tabLabel} />
        ))}
      </Tabs>
      <Box sx={{ p: 3 }}>
        {view === "list" && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {cfg.fullLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cfg.sub}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={fetchList}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
                {CAN_ADD.includes(role) && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openNew}
                  >
                    Add
                  </Button>
                )}
              </Box>
            </Box>
            {listLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <LoadingSpinner size={30} />
              </Box>
            ) : (
              <TableComponent
                columns={[...(TC[cfg.num] || []), actionCol]}
                rows={tableRows}
                manageColumnStatus
                enableSort={true}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    fontWeight: "bold",
                  },
                }}
              />
            )}
          </>
        )}
        {view === "form" && (
          <>
            <Button size="small" onClick={() => setView("list")} sx={{ mb: 2 }}>
              ← Back to List
            </Button>
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Government of Karnataka
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                SPLOCA Public Record Forms
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold">
                {cfg.fullLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {cfg.sub}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {renderFields(false)}
            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button variant="outlined" onClick={() => setView("list")}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                {selected ? "Update" : "Save Draft"}
              </Button>
            </Box>
          </>
        )}
        {view === "detail" && selected && (
          <>
            <Button size="small" onClick={() => setView("list")} sx={{ mb: 2 }}>
              ← Back to List
            </Button>
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Government of Karnataka
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                SPLOCA Public Record Forms
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold">
                {cfg.fullLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {cfg.sub}
              </Typography>
              {/* <Box sx={{ mt: 1 }}>
                <StatusChip status={selected.status} />
              </Box> */}
            </Box>
            <Divider sx={{ mb: 2 }} />
            {renderFields(true)}
            <ApprovalTimeline form={selected} flowType="public" />
            <ApprovalPanel
              form={selected}
              apiPath={cfg.ep}
              role={role}
              flowType="public"
              onRefresh={() => openView(selected)}
            />
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button variant="outlined" onClick={() => setView("list")}>
                Back to List
              </Button>
              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={() => window.print()}
              >
                Print
              </Button>
            </Box>
          </>
        )}
      </Box>
      <SnackBarComponent
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};
